/**
 * One shared palette + line style for the whole game, tuned to match the
 * "Scoops & Smiles" asset kit: pastel sky, pink-striped awning, glossy
 * speckled scoops, a warm wood counter, thick warm-brown outlines.
 *
 * TWEAK ME: change a flavor color here and it updates the scoop art, the
 * order bubble, and the ingredient buttons everywhere — one source of truth.
 */

/** Warm dark-brown used for every outline, so all art feels like one set. */
export const INK = '#4a3526';

/** Outline thickness, in the SVG's own logical units (see primitives.ts). */
export const STROKE = 6;

/** Softer outline (sprinkles, fine detail) so small marks don't go muddy. */
export const STROKE_THIN = 4;

/** Speckle/chip rendering style on a scoop. */
export type SpeckStyle = 'dot' | 'chunk' | 'none';

/** An ice-cream flavor. `id` is a stable key across the game. */
export interface Flavor {
  id: string;
  /** Main scoop fill. */
  color: string;
  /** Seeds / chips / specks color. */
  speck: string;
  /** How the specks are drawn (round seeds, square chunks, or none). */
  speckStyle: SpeckStyle;
  /** Speck radius (dot) or half-size (chunk), in scoop-logical px. */
  speckSize: number;
}

/** The eight flavors offered across the game (colors from the asset kit). */
export const FLAVORS: Flavor[] = [
  { id: 'strawberry', color: '#F7A6C2', speck: '#E879A6', speckStyle: 'dot', speckSize: 5 },
  { id: 'chocolate', color: '#8A5A33', speck: '#5E3A1E', speckStyle: 'dot', speckSize: 6 },
  { id: 'vanilla', color: '#F4E8C4', speck: '#DEC98C', speckStyle: 'dot', speckSize: 3 },
  { id: 'mint', color: '#A9E0C2', speck: '#5E3A1E', speckStyle: 'chunk', speckSize: 5 },
  { id: 'blueberry', color: '#AC92E2', speck: '#7E63C4', speckStyle: 'dot', speckSize: 6 },
  { id: 'bubblegum', color: '#9FD7F0', speck: '#F4A0C0', speckStyle: 'dot', speckSize: 5.5 },
  { id: 'mango', color: '#F6C25E', speck: '#E29B2E', speckStyle: 'dot', speckSize: 4.5 },
  { id: 'cookies', color: '#F1ECE0', speck: '#3A2A20', speckStyle: 'chunk', speckSize: 6 },
];

export const FLAVOR_BY_ID: Record<string, Flavor> = Object.fromEntries(
  FLAVORS.map((f) => [f.id, f]),
);

/** Coin values used in the shop. Distinguished by BOTH color and dot-count. */
export interface CoinKind {
  value: number;
  color: string;
  light: string;
  rim: string;
}

export const COINS: CoinKind[] = [
  { value: 1, color: '#B5762E', light: '#D89A52', rim: '#7C4E18' }, // bronze token
  { value: 2, color: '#CBD0D6', light: '#EAEEF1', rim: '#8C939C' }, // silver token
  { value: 5, color: '#F4C53A', light: '#FCE08A', rim: '#C79516' }, // gold token
];

export const COIN_BY_VALUE: Record<number, CoinKind> = Object.fromEntries(
  COINS.map((c) => [c.value, c]),
);

/** Background, surfaces, and UI accents — all from the asset-kit palette. */
export const UI = {
  // Sky + scenery
  skyTop: '#8FD3F5',
  skyBottom: '#D9EFFB',
  cloud: '#ffffff',
  cloudShade: '#dceffa',
  hedge: '#7FC25A',
  hedgeDark: '#6BB048',
  hedgeLight: '#9AD884',
  fence: '#ffffff',
  fenceShade: '#d2dae0',
  flowerPink: '#F48FB1',
  flowerYellow: '#FCD24B',

  // Awning + sign
  awning: '#F26C97',
  awningDark: '#D9547F',
  awningStripe: '#ffffff',
  awningBar: '#F8A9C2',
  sign: '#FFF6DB',
  signEdge: '#EF88AB',
  heart: '#F48FB1',

  // Counter / wood
  counter: '#E2C488',
  counterShade: '#C9A86A',
  counterTop: '#F1DCAE',
  counterSeam: '#E0C488',
  shelfBorder: '#F48FB1',
  shelfBorderDark: '#D9547F',

  // Tiles / panels
  tile: '#ffffff',
  tileEdge: '#E2D3C0',
  tileShade: '#f3ead3',
  panel: '#FBEFD9',
  panelEdge: '#E9D2A0',
  board: '#FFF6DB',
  boardEdge: '#EF9AB4',

  // Ice cream parts
  cone: '#E8B86A',
  coneDark: '#C9924A',
  coneLight: '#F0C97E',
  cupBody: '#F48FB1',
  cupBand: '#F7A8C4',
  cupLight: '#F9BBD1',

  // Plate / dish
  plate: '#8FD3F5',
  plateLight: '#BFE6F8',
  plateRim: '#6FB6DE',

  // Toppings
  cherry: '#E5443B',
  cherryLight: '#F26B62',
  cherryStem: '#6FB23A',
  cherryLeaf: '#7CC243',
  wafer: '#EBCB9C',
  waferDark: '#9C6B38',
  waferEnd: '#D9A86C',
  whip: '#FFFDF6',
  whipShade: '#D8C7A8',

  // Jar
  jarGlass: '#CFEAF6',
  jarGlassShade: '#AFDCEC',
  jarGlassEdge: '#8FBFD4',
  jarLid: '#F48FB1',
  jarLidDark: '#D96F93',

  // Buttons / icons
  bell: '#FCD24B',
  bellDark: '#E0A92E',
  bellBody: '#FFE08A',
  star: '#FCD24B',
  starShade: '#E0A92E',
  refresh: '#4AA3E8',
  refreshDark: '#2F7CB5',
  sound: '#2BA8C4',
  soundDark: '#1F7E95',
  btnFace: '#ffffff',
  serve: '#FCD24B',

  // Speech bubble
  bubble: '#ffffff',
  bubbleEdge: '#E2D3C0',
} as const;

/** Sprinkle colors — bright primaries scattered on a topping tile. */
export const SPRINKLE_COLORS = ['#F25C5C', '#FCD24B', '#4AA3E8', '#5FBF6A', '#F48FB1', '#AC92E2', '#F39B3E'];
